import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, CheckCircle2, MinusCircle } from "lucide-react";
import { toast } from "sonner";


const ClientPicker = ({ entities, selected, toggle, onAdded }) => {
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const shown = entities.filter((e) => e.name.toLowerCase().includes(q.toLowerCase()));
  const add = async () => {
    const name = q.trim();
    if (!name) return;
    setAdding(true);
    try {
      const { data } = await api.post("/entities", { name });
      onAdded(data);
      setQ("");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setAdding(false);
    }
  };
  const exact = entities.some((e) => e.name.toLowerCase() === q.trim().toLowerCase());
  return (
    <div className="border border-slate-200 rounded-md">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200">
        <Search className="w-4 h-4 text-slate-400" />
        <input data-testid="cc-client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search or type a new client / entity"
          className="flex-1 text-sm outline-none bg-transparent" />
        {q.trim() && !exact && (
          <Button size="sm" variant="outline" data-testid="cc-client-add" disabled={adding} onClick={add} className="gap-1 h-7">
            <Plus className="w-3 h-3" />Add “{q.trim()}”
          </Button>
        )}
      </div>
      <div className="max-h-44 overflow-y-auto p-2 grid sm:grid-cols-2 gap-1" data-testid="cc-client-list">
        {shown.length === 0 && <div className="text-xs text-slate-500 p-2">No entities yet — type a name above and click Add.</div>}
        {shown.map((e) => (
          <label key={e.id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
            <Checkbox data-testid={`cc-client-${e.id}`} checked={selected.includes(e.name)} onCheckedChange={() => toggle(e.name)} />
            <span className="truncate">{e.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const GroupMatrix = ({ clients, groups, counts, matrix, setCell, setColumn }) => (
  <div className="border border-slate-200 rounded-md overflow-x-auto" data-testid="cc-matrix">
    <table className="text-xs w-full">
      <thead>
        <tr className="bg-slate-50 text-slate-600">
          <th className="text-left font-medium px-3 py-2 sticky left-0 bg-slate-50 min-w-[160px]">Client</th>
          {Object.keys(groups).map((g) => (
            <th key={g} className="font-medium px-2 py-2 text-center min-w-[92px]">
              <div>{groups[g]}</div>
              <div className="text-[10px] text-slate-400 font-normal">{counts[g] || 0} items</div>
            </th>
          ))}
        </tr>
        <tr className="border-b border-slate-200">
          <td className="px-3 py-1.5 text-[11px] text-slate-500 sticky left-0 bg-white">Tick for all clients</td>
          {Object.keys(groups).map((g) => (
            <td key={g} className="text-center py-1.5">
              <Checkbox data-testid={`cc-col-${g}`} checked={clients.length > 0 && clients.every((c) => matrix[c]?.includes(g))}
                onCheckedChange={(v) => setColumn(g, v === true)} />
            </td>
          ))}
        </tr>
      </thead>
      <tbody>
        {clients.map((c, ci) => (
          <tr key={c} className="border-b border-slate-100 last:border-0">
            <td className="px-3 py-2 font-medium sticky left-0 bg-white truncate max-w-[200px]">{c}</td>
            {Object.keys(groups).map((g) => (
              <td key={g} className="text-center py-2">
                <Checkbox data-testid={`cc-cell-${ci}-${g}`} checked={!!matrix[c]?.includes(g)} onCheckedChange={() => setCell(c, g)} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ResultSummary = ({ result }) => (
  <div className="space-y-3" data-testid="cc-result">
    <div className="flex items-center gap-2 text-sm">
      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
      <span><b data-testid="cc-created-total">{result.created_total}</b> reminders created for {result.owner.name}
        {result.skipped_total > 0 && <> · <b data-testid="cc-skipped-total">{result.skipped_total}</b> skipped (already exist)</>}</span>
    </div>
    <div className="max-h-80 overflow-y-auto space-y-3">
      {result.clients.map((c) => (
        <div key={c.entity} className="border border-slate-200 rounded-md p-3">
          <div className="text-sm font-semibold">{c.entity} <span className="text-xs text-slate-500 font-normal">— {c.created.length} created</span></div>
          <ul className="mt-2 text-xs text-slate-600 space-y-0.5">
            {c.created.map((o) => <li key={o.name} className="flex justify-between gap-3"><span>{o.name}</span><span className="mono">{o.next_due_date} · {o.frequency}</span></li>)}
            {c.skipped.map((n) => <li key={n} className="flex items-center gap-1 text-slate-400"><MinusCircle className="w-3 h-3" />{n} — already exists</li>)}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

export default function ComplianceCalendarDialog({ open, onOpenChange, onDone }) {
  const { user } = useAuth();
  const canPickOwner = ["admin", "top_management"].includes(user?.role);
  const [template, setTemplate] = useState(null);
  const [entities, setEntities] = useState([]);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [ownerId, setOwnerId] = useState("");
  const [days, setDays] = useState("7");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, e, m] = await Promise.all([api.get("/compliance/template"), api.get("/entities"),
        canPickOwner ? api.get("/users/assignable") : Promise.resolve({ data: [] })]);
      setTemplate(t.data);
      setEntities(e.data);
      setMembers(m.data);
    } catch (err) {
      toast.error(formatErr(err));
    }
  }, [canPickOwner]);

  useEffect(() => {
    if (!open) return;
    setSelected([]); setMatrix({}); setResult(null); setOwnerId(user?.id || ""); setDays("7");
    load();
  }, [open, load, user?.id]);

  const counts = useMemo(() => (template?.items || []).filter((i) => i.enabled).reduce((a, i) => ({ ...a, [i.group]: (a[i.group] || 0) + 1 }), {}), [template]);
  const toggleClient = (name) => {
    setSelected((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));
    setMatrix((m) => (m[name] ? m : { ...m, [name]: [] }));
  };
  const setCell = (c, g) => setMatrix((m) => ({ ...m, [c]: m[c]?.includes(g) ? m[c].filter((x) => x !== g) : [...(m[c] || []), g] }));
  const setColumn = (g, on) => setMatrix((m) => Object.fromEntries(selected.map((c) => {
    const cur = (m[c] || []).filter((x) => x !== g);
    return [c, on ? [...cur, g] : cur];
  })));
  const total = selected.reduce((n, c) => n + (matrix[c] || []).reduce((k, g) => k + (counts[g] || 0), 0), 0);

  const apply = async () => {
    setBusy(true);
    try {
      const clients = selected.filter((c) => matrix[c]?.length).map((c) => ({ entity: c, groups: matrix[c] }));
      const { data } = await api.post("/compliance/apply", { clients, owner_id: ownerId || null, reminder_days_before: Number(days) || 0 });
      setResult(data);
      toast.success(`${data.created_total} compliance reminders added`);
      onDone?.();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="compliance-calendar-dialog">
        <DialogHeader>
          <DialogTitle>Add compliance calendar</DialogTitle>
          <DialogDescription>Pick one or more clients, tick what applies to each, and we'll create reminder obligations (no amount) that repeat on the standard Indian due dates.</DialogDescription>
        </DialogHeader>
        {result ? <ResultSummary result={result} /> : (
          <div className="space-y-5">
            <div className="grid md:grid-cols-3 gap-4">
              {canPickOwner && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Owner (gets the reminders)</Label>
                  <Select value={ownerId} onValueChange={setOwnerId}>
                    <SelectTrigger data-testid="cc-owner" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.id === user.id ? " (me)" : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Remind days before</Label>
                <Input data-testid="cc-days" type="number" min={0} max={60} value={days} onChange={(e) => setDays(e.target.value)} className="mt-1 mono" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">1 · Clients / entities</Label>
              <div className="mt-1"><ClientPicker entities={entities} selected={selected} toggle={toggleClient}
                onAdded={(e) => { setEntities((l) => [...l, e]); toggleClient(e.name); }} /></div>
            </div>
            {selected.length > 0 && template && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">2 · What applies to each client</Label>
                <div className="mt-1"><GroupMatrix clients={selected} groups={template.groups} counts={counts} matrix={matrix} setCell={setCell} setColumn={setColumn} /></div>
              </div>
            )}
            <p className="text-[11px] text-slate-500">Due dates follow the standard statutory calendar. When the government extends a date, edit the affected obligations. Admins can change the list in Compliance → Calendar setup.</p>
          </div>
        )}
        <DialogFooter>
          {result ? (
            <Button data-testid="cc-done" onClick={() => onOpenChange(false)} className="bg-blue-700 hover:bg-blue-800 text-white">Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cc-cancel">Cancel</Button>
              <Button data-testid="cc-apply" disabled={busy || total === 0} onClick={apply} className="bg-blue-700 hover:bg-blue-800 text-white">
                {busy ? "Creating…" : `Create ${total} reminder${total === 1 ? "" : "s"}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
